import { COLORS } from "../hooks/useAudioRecorder";

/**
 * Sticky-note style example phrases on the Home screen, teaching new users
 * the rough cadence of what to say. Yellow paper, dark text, mono font,
 * fixed slight rotations for the post-it feel.
 *
 * Renders only when isIdle so they don't crowd the active recording state.
 * On mobile the cards stack vertically (no overlap — easier to read).
 */
const CARDS = [
  {
    label: "// SIMPLE",
    body: "Fixed Maria's leaky faucet, 2 hours at 80 dollars, parts 25 dollars",
    rotate: -1.5,
  },
  {
    label: "// WITH TAX",
    body: "Cleared the kitchen drain at the Smith's, 1.5 hours at 90, plus 8 percent tax",
    rotate: 1.2,
  },
  {
    label: "// FLAT FEE",
    body: "Replaced water heater for John, flat 450 with 20 dollar disposal fee",
    rotate: -0.7,
  },
  {
    label: "// MATERIALS",
    body: "Installed faucet at 123 Oak Street, 2 hours at 75, faucet from Home Depot 80, sealant 12",
    rotate: 1.8,
  },
  {
    label: "// DISCOUNT",
    body: "Patched the drywall for the Garcias, flat 180, and knock 20 off for a repeat customer",
    rotate: -1.3,
  },
];

export default function TutorialCards() {
  return (
    <section
      data-testid="home-tutorial-cards"
      style={{
        // Padding-x clears the rotated card extents so they don't trigger
        // horizontal scroll on narrow viewports.
        padding: "28px 24px 18px",
        marginTop: 8,
      }}
    >
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          letterSpacing: "0.06em",
          color: COLORS.faint,
          textAlign: "center",
          marginBottom: 18,
        }}
      >
        // say something like…
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 16,
          overflow: "visible",
        }}
      >
        <OnboardingCard />
        {CARDS.map((card, i) => (
          <StickyCard key={i} {...card} />
        ))}
      </div>
    </section>
  );
}

/**
 * First card in the row. Deliberately NOT a yellow example card — dark panel
 * with a dashed yellow border so it reads as "instructions, not an example".
 * Always rendered, no auth/profile conditional logic by design.
 */
const ONBOARDING_STEPS = [
  "1 · ⚙ add your business in settings",
  "2 · sign in to save invoices",
  "3 · tap record and talk",
];

const OnboardingCard = () => {
  const rotate = -2.2;
  return (
    <div
      style={{
        width: 200,
        maxWidth: "100%",
        flex: "0 0 auto",
        backgroundColor: "#1a1917",
        border: "1.5px dashed #e8dc2d",
        borderRadius: 3,
        padding: "14px 16px",
        boxShadow:
          "0 10px 20px -4px rgba(0,0,0,0.45), 0 3px 6px rgba(0,0,0,0.25)",
        transform: `rotate(${rotate}deg)`,
        transition: "transform 240ms ease",
      }}
      onMouseOver={(e) => (e.currentTarget.style.transform = "rotate(0deg)")}
      onMouseOut={(e) =>
        (e.currentTarget.style.transform = `rotate(${rotate}deg)`)
      }
    >
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "#e8dc2d",
          marginBottom: 8,
        }}
      >
        // first time?
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          lineHeight: 1.6,
          color: "#f4f1ea",
        }}
      >
        {ONBOARDING_STEPS.map((step, i) => (
          <div key={i}>{step}</div>
        ))}
      </div>
    </div>
  );
};

const StickyCard = ({ label, body, rotate }) => (
  <div
    style={{
      width: 200,
      maxWidth: "100%",
      flex: "0 0 auto",
      backgroundColor: "#e8dc2d",
      color: "#0f0e0c",
      borderRadius: 3,
      padding: "14px 16px",
      boxShadow:
        "0 10px 20px -4px rgba(0,0,0,0.45), 0 3px 6px rgba(0,0,0,0.25)",
      transform: `rotate(${rotate}deg)`,
      transition: "transform 240ms ease",
    }}
    onMouseOver={(e) => (e.currentTarget.style.transform = "rotate(0deg)")}
    onMouseOut={(e) => (e.currentTarget.style.transform = `rotate(${rotate}deg)`)}
  >
    <div
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 9,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: "rgba(15, 14, 12, 0.6)",
        marginBottom: 8,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 12,
        lineHeight: 1.5,
        color: "#0f0e0c",
      }}
    >
      “{body}”
    </div>
  </div>
);
